import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, buttonClassName } from '@safetyhub/ui';
import {
  adminCompaniesApi,
  extractApiError,
  type AdminMergeRequestItem,
  type AdminMergeRequestStatus,
} from '../lib/api';
import { Badge, type BadgeColor } from './Badge';
import { useToastStore } from '../stores/toast';

const STATUS_LABEL: Partial<Record<AdminMergeRequestStatus, string>> = {
  ACCEPTED: '수락됨 · 통합 미완',
  REJECTED: '거절됨',
  EXPIRED: '기간 만료',
};

const STATUS_COLOR: Partial<Record<AdminMergeRequestStatus, BadgeColor>> = {
  ACCEPTED: 'orange',
  REJECTED: 'red',
  EXPIRED: 'gray',
};

/** 중재 창에서 고르는 두 결론. 되돌릴 수 없어 둘 다 확인을 받는다. */
type PendingAction = { kind: 'execute' | 'dismiss'; item: AdminMergeRequestItem };

/**
 * 테넌트 통합 요청 중재 큐 (P4-B).
 *
 * 통합은 대상 조직 소유자의 명시적 수락으로만 진행된다. 그래서 사람 손이 필요한 건이 세 갈래로
 * 남는다 — 수락은 했는데 흡수 엔진이 실패해 멈춘 건(ACCEPTED), 거절된 건, 응답 없이 만료된 건.
 * 앞의 하나는 **재시도**가 답이고 뒤의 둘은 사람이 사정을 듣고 진행/기각을 정해야 한다.
 *
 * 중재 대상이 없으면 통째로 사라진다(MergeBacklogSection과 같은 규약 — 평소 비어 있는 게 정상).
 */
export default function TenantMergeRequestSection() {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const limit = 10;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies', 'merge-requests', page],
    queryFn: () => adminCompaniesApi.mergeRequests({ page, limit }),
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'companies', 'merge-requests'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
  };

  const executeMutation = useMutation({
    mutationFn: (requestId: string) => adminCompaniesApi.executeMergeRequest(requestId),
    onSuccess: () => {
      showToast('통합을 실행했습니다.', 'success');
      setPending(null);
      invalidate();
    },
    onError: (err) => {
      // 엔진 실패는 전체 롤백이라 상태가 그대로 남는다 — 큐에서 사라지지 않는 게 정상이다.
      showToast(extractApiError(err).message, 'error');
      setPending(null);
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (requestId: string) => adminCompaniesApi.dismissMergeRequest(requestId),
    onSuccess: () => {
      showToast('요청을 기각했습니다.', 'success');
      setPending(null);
      invalidate();
    },
    onError: (err) => {
      showToast(extractApiError(err).message, 'error');
      setPending(null);
    },
  });

  const isBusy = executeMutation.isPending || dismissMutation.isPending;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // 마지막 페이지의 건을 실행/기각하면 그 페이지가 사라진다 — 남은 마지막 페이지로 당겨오지
  // 않으면 빈 표만 보이고, 페이지 이동 버튼은 totalPages가 1로 줄어 함께 사라진다.
  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  // 못 읽은 것과 비어 있는 것을 구분한다 — 중재 건을 못 보는 것이 이 섹션이 막으려는 상태다.
  if (error) {
    return (
      <Card radius="lg" className="mb-4">
        <p className="text-xs text-red-600">통합 요청 중재 목록을 불러오지 못했습니다.</p>
      </Card>
    );
  }

  if (isLoading || total === 0) return null;

  const confirmAction = () => {
    if (!pending) return;
    if (pending.kind === 'execute') executeMutation.mutate(pending.item.id);
    else dismissMutation.mutate(pending.item.id);
  };

  return (
    <Card radius="lg" className="mb-4">
      <div className="rounded border border-orange-200 bg-orange-50 p-3 mb-3">
        <h3 className="text-sm font-semibold text-orange-900">
          조직 통합 중재 {total.toLocaleString('ko-KR')}건
        </h3>
        <p className="mt-1 text-xs text-orange-800">
          수락됐지만 통합 처리가 끝나지 않은 건(재시도 필요)과, 거절·만료로 멈춘 건입니다. 실행은
          대상 조직을 정본으로 통째 흡수하며 되돌릴 수 없습니다 — 거절·만료 건은 사정을 확인한
          뒤에만 실행하세요.
        </p>
      </div>

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">상태</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                요청 조직 (정본)
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                대상 조직 (소멸)
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">사유 / 시각</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">중재</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge
                    color={STATUS_COLOR[item.status] ?? 'gray'}
                    label={STATUS_LABEL[item.status] ?? item.status}
                  />
                </td>
                <td className="px-3 py-2 text-xs text-gray-900">
                  <span className="font-medium">{item.canonicalCompany?.name ?? '-'}</span>
                  <span className="block font-mono text-gray-500">
                    {item.canonicalCompany?.registrationNumber ?? '-'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-900">
                  <span className="font-medium">{item.targetCompany?.name ?? '-'}</span>
                  <span className="block font-mono text-gray-500">
                    {item.targetCompany?.registrationNumber ?? '-'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {item.rejectReason ? (
                    <span className="block">{item.rejectReason}</span>
                  ) : (
                    <span className="block text-gray-400">사유 없음</span>
                  )}
                  <span className="block text-gray-500">
                    {new Date(item.resolvedAt ?? item.createdAt).toLocaleString('ko-KR')}
                  </span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => setPending({ kind: 'execute', item })}
                    >
                      실행
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => setPending({ kind: 'dismiss', item })}
                    >
                      기각
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-xs">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-40"
          >
            이전
          </button>
          <span className="text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-40"
          >
            다음
          </button>
        </div>
      )}

      {/* 처리 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전 `preventClose`와 같은 축.
          강제 실행은 되돌릴 수 없는 danger라 첫 포커스를 「취소」에 둔다(Enter 오조작 방지 —
          `Button`은 ref를 넘기지 않아 취소만 같은 정본 클래스의 네이티브 버튼이다). */}
      <Modal
        open={pending !== null}
        onClose={() => {
          if (!isBusy) setPending(null);
        }}
        title={pending?.kind === 'execute' ? '통합 강제 실행' : '통합 요청 기각'}
        size="md"
        initialFocusRef={pending?.kind === 'execute' ? cancelRef : undefined}
        closeOnOverlay={!isBusy}
        closeOnEsc={!isBusy}
        showCloseButton={!isBusy}
        closeLocked={isBusy}
      >
        {pending && (
          <>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <strong>{pending.item.canonicalCompany?.name ?? '요청 조직'}</strong>
                {' ← '}
                <strong>{pending.item.targetCompany?.name ?? '대상 조직'}</strong>
              </p>
              {pending.kind === 'execute' ? (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 leading-relaxed">
                  대상 조직의 그룹·현장·구성원·점검·보고서가 모두 정본 조직으로 옮겨지고 대상
                  조직은 삭제됩니다. 대상 조직의 구독은 해지됩니다.
                  <br />
                  <strong>되돌릴 수 없습니다.</strong>
                  {pending.item.status !== 'ACCEPTED' && (
                    <>
                      <br />
                      이 건은 <strong>{STATUS_LABEL[pending.item.status]}</strong> 상태입니다 —
                      대상 조직의 동의 없이 실행하는 것이므로 사정을 확인한 뒤 진행하세요.
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 leading-relaxed">
                  이 요청을 중재 목록에서 내립니다(취소 처리). 조직은 양쪽 모두 그대로 유지되며,
                  필요하면 정본 조직이 통합을 다시 요청할 수 있습니다.
                </div>
              )}
              <p className="text-xs text-gray-500">이 작업은 감사 로그에 기록됩니다.</p>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                ref={cancelRef}
                type="button"
                className={buttonClassName('secondary', 'sm')}
                onClick={() => setPending(null)}
                disabled={isBusy}
              >
                취소
              </button>
              <Button
                variant={pending.kind === 'execute' ? 'danger' : 'primary'}
                size="sm"
                onClick={confirmAction}
                disabled={isBusy}
              >
                {isBusy ? '처리 중...' : pending.kind === 'execute' ? '실행' : '기각'}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </Card>
  );
}
